import { z } from 'zod';

/**
 * A lab's public page and the bookings people make through it. The rules here are shared by the
 * API, the web app and (mirrored in Kotlin) the Android app, so all three agree on when a lab is
 * open, which times can be booked and what a booking must contain.
 */

/** Monday first, as Indian labs write their timings. */
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const Time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a time like 08:30');

export const DayHours = z.object({ open: z.boolean(), from: Time, to: Time });
export type DayHours = z.infer<typeof DayHours>;

/** What most labs keep: Monday to Saturday 7:30 to 8 in the evening, Sunday morning. */
export const DEFAULT_HOURS: DayHours[] = [
  ...Array.from({ length: 6 }, () => ({ open: true, from: '07:30', to: '20:00' })),
  { open: true, from: '08:00', to: '13:00' },
];

/** Things people look for in a lab, offered as ticks; a lab can add its own. */
export const FACILITY_SUGGESTIONS = [
  'NABL accredited',
  'Online reports',
  'UPI and card payments',
  'Wheelchair access',
  'Parking',
  'Air-conditioned waiting area',
  'Female staff for collection',
  'Doctor consultation',
  'Health packages',
  'ECG',
  'X-ray',
  'Ultrasound',
] as const;

const Paise = z.number().int().min(0).max(10_000_000);

/** What a lab saves for its public page. */
export const ListingInput = z.object({
  enabled: z.boolean(),
  name: z.string().trim().max(120),
  about: z.string().trim().max(1500),
  address: z.string().trim().max(400),
  area: z.string().trim().max(80),
  city: z.string().trim().max(60),
  pincode: z
    .string()
    .trim()
    .regex(/^(\d{6})?$/, 'A PIN code has 6 digits'),
  phone: z.string().trim().max(60),
  whatsapp: z.string().trim().max(20),
  email: z.string().trim().max(120),
  logoFileId: z.uuid().nullable(),
  sameAsReport: z.object({
    name: z.boolean(),
    address: z.boolean(),
    phone: z.boolean(),
    email: z.boolean(),
    logo: z.boolean(),
  }),
  mapsUrl: z.string().trim().max(600),
  homeCollection: z.boolean(),
  homeCollectionFeePaise: Paise,
  homeCollectionKm: z.number().int().min(0).max(200),
  reportDelivery: z.boolean(),
  reportDeliveryFeePaise: Paise,
  acceptsBookings: z.boolean(),
  open24h: z.boolean(),
  hours: z.array(DayHours).length(7),
  closedDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(120),
  slotMinutes: z.number().int().min(10).max(240),
  /** People one slot takes at the lab, and for home collection; 0 = no limit. */
  slotCapacity: z.number().int().min(0).max(500),
  homeSlotCapacity: z.number().int().min(0).max(500),
  facilities: z.array(z.string().trim().min(1).max(60)).max(30),
  hidePrices: z.boolean(),
  hiddenTemplates: z.array(z.string()).max(5000),
  hiddenPrices: z.array(z.string()).max(5000),
});
export type ListingInput = z.infer<typeof ListingInput>;

export const DEFAULT_LISTING: ListingInput = {
  enabled: false,
  name: '',
  about: '',
  address: '',
  area: '',
  city: '',
  pincode: '',
  phone: '',
  whatsapp: '',
  email: '',
  logoFileId: null,
  sameAsReport: { name: true, address: true, phone: true, email: true, logo: true },
  mapsUrl: '',
  homeCollection: false,
  homeCollectionFeePaise: 0,
  homeCollectionKm: 0,
  reportDelivery: false,
  reportDeliveryFeePaise: 0,
  acceptsBookings: true,
  open24h: false,
  hours: DEFAULT_HOURS,
  closedDates: [],
  slotMinutes: 30,
  slotCapacity: 5,
  homeSlotCapacity: 2,
  facilities: [],
  hidePrices: false,
  hiddenTemplates: [],
  hiddenPrices: [],
};

/** What is missing before the page can be shown publicly, in words; empty when it is ready. */
export function listingProblems(l: {
  name: string;
  address: string;
  city: string;
  phone: string;
}): string[] {
  const out: string[] = [];
  if (!l.name.trim()) out.push('Add the lab name');
  if (!l.address.trim()) out.push('Add the address');
  if (!l.city.trim()) out.push('Add the city');
  if (!l.phone.trim()) out.push('Add a phone number');
  return out;
}

// ---- time ----------------------------------------------------------------------------------

/** Labs work in Indian time; the server and phones may not. */
const IST_OFFSET_MIN = 330;

/** The date ('YYYY-MM-DD') and minute of the day in India at this moment. */
export function indiaNow(now: Date = new Date()): {
  date: string;
  minute: number;
  weekday: number;
} {
  const t = new Date(now.getTime() + IST_OFFSET_MIN * 60_000);
  const date = t.toISOString().slice(0, 10);
  return {
    date,
    minute: t.getUTCHours() * 60 + t.getUTCMinutes(),
    weekday: weekdayOf(date),
  };
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayOf(date: string): number {
  const d = new Date(`${date}T00:00:00Z`).getUTCDay();
  return (d + 6) % 7;
}

function addDaysTo(date: string, days: number): string {
  const t = new Date(`${date}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
}

const minutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const hhmm = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

interface Timings {
  open24h: boolean;
  hours: DayHours[];
  closedDates: string[];
}

/** Is the lab open on this date at all? */
export function openOn(l: Timings, date: string): boolean {
  if (l.closedDates.includes(date)) return false;
  if (l.open24h) return true;
  return l.hours[weekdayOf(date)]?.open === true;
}

/** Is the lab open right now (Indian time)? */
export function openNow(l: Timings, now: Date = new Date()): boolean {
  const n = indiaNow(now);
  if (!openOn(l, n.date)) return false;
  if (l.open24h) return true;
  const h = l.hours[n.weekday] as DayHours;
  return n.minute >= minutes(h.from) && n.minute < minutes(h.to);
}

/** "Open till 8 pm", "Opens 7:30 am tomorrow", "Closed today" and the like. */
export function openLabel(l: Timings, now: Date = new Date()): string {
  if (l.open24h && !l.closedDates.includes(indiaNow(now).date)) return 'Open 24 hours';
  const n = indiaNow(now);
  const today = l.hours[n.weekday] as DayHours;
  if (openNow(l, now)) return `Open till ${clock(today.to)}`;
  if (openOn(l, n.date) && n.minute < minutes(today.from)) return `Opens ${clock(today.from)}`;
  for (let i = 1; i <= 7; i++) {
    const d = addDaysTo(n.date, i);
    if (openOn(l, d)) {
      const h = l.hours[weekdayOf(d)] as DayHours;
      const when = i === 1 ? 'tomorrow' : WEEKDAYS[weekdayOf(d)];
      return `Closed · opens ${l.open24h ? '' : `${clock(h.from)} `}${when}`.replace('  ', ' ');
    }
  }
  return 'Closed';
}

/** 08:30 → 8:30 am, 20:00 → 8 pm. */
export function clock(t: string): string {
  const m = minutes(t);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${mm ? `:${String(mm).padStart(2, '0')}` : ''} ${h < 12 ? 'am' : 'pm'}`;
}

/**
 * The times that can be booked on a date: one every `slotMinutes` while the lab is open, the
 * last one starting a slot before closing. Today's slots start an hour from now, so the lab has
 * time to see the booking.
 */
export function slotsOn(
  l: Timings & { slotMinutes: number },
  date: string,
  now: Date = new Date(),
): string[] {
  if (!openOn(l, date)) return [];
  const h = l.hours[weekdayOf(date)] as DayHours;
  const from = l.open24h ? 7 * 60 : minutes(h.from);
  const to = l.open24h ? 21 * 60 : minutes(h.to);
  const n = indiaNow(now);
  if (date < n.date) return [];
  const earliest = date === n.date ? n.minute + 60 : 0;
  const out: string[] = [];
  for (let m = from; m + l.slotMinutes <= to; m += l.slotMinutes) {
    if (m >= earliest) out.push(hhmm(m));
  }
  return out;
}

/**
 * How many bookings of this kind one slot takes. Visits at the lab and home collections fill
 * separately: the counter and the collectors are not the same people. No limit when 0.
 */
export function slotCapacity(
  l: { slotCapacity: number; homeSlotCapacity: number },
  kind: 'visit' | 'home',
): number {
  const n = kind === 'home' ? l.homeSlotCapacity : l.slotCapacity;
  return n > 0 ? n : Number.POSITIVE_INFINITY;
}

/** How far ahead people can book. */
export const BOOKING_DAYS_AHEAD = 30;

// ---- place ---------------------------------------------------------------------------------

/**
 * Coordinates written in a Google Maps link, if any: ".../@34.08,74.79,17z", "?q=34.08,74.79",
 * "!3d34.08!4d74.79", "ll=…", "query=…" or "destination=…". Short links (maps.app.goo.gl) carry
 * none; the server follows them first.
 */
export function coordinatesIn(url: string): { lat: number; lng: number } | null {
  const text = decodeURIComponent(url);
  const patterns = [
    /!3d(-?\d{1,2}\.\d+)!4d(-?\d{1,3}\.\d+)/,
    /@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/,
    /[?&](?:q|ll|query|destination|center|daddr)=(?:loc:)?\s*(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/,
  ];
  for (const p of patterns) {
    const m = p.exec(text);
    if (m) {
      const lat = Number(m[1]);
      const lng = Number(m[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
    }
  }
  return null;
}

/** Is this a Google Maps link (the only kind the server will follow)? */
export function isMapsLink(url: string): boolean {
  // Kept free of the URL class: this package is shared with code that has no DOM types.
  const m = /^https:\/\/([a-z0-9.-]+)(?:[/?#]|$)/i.exec(url.trim());
  const host = m?.[1]?.toLowerCase() ?? '';
  return [
    'maps.app.goo.gl',
    'goo.gl',
    'maps.google.com',
    'www.google.com',
    'google.com',
    'maps.google.co.in',
    'www.google.co.in',
    'google.co.in',
  ].includes(host);
}

/** Straight-line distance in km. */
export function distanceKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const r = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(s));
}

// ---- bookings ------------------------------------------------------------------------------

export const BOOKING_STATUSES = ['pending', 'approved', 'declined', 'cancelled', 'done'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  pending: 'Waiting for the lab',
  approved: 'Confirmed',
  declined: 'Declined',
  cancelled: 'Cancelled',
  done: 'Completed',
};

/** What someone sends to book. */
export const BookingRequest = z.object({
  labId: z.uuid(),
  kind: z.enum(['visit', 'home']),
  templateIds: z.array(z.uuid()).min(1, 'Choose at least one test').max(40),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  time: Time,
  patientName: z.string().trim().min(1, 'Enter the patient name').max(80),
  patientAge: z.string().trim().max(20).default(''),
  patientSex: z.enum(['M', 'F', 'O', '']).default(''),
  address: z.string().trim().max(400).default(''),
  note: z.string().trim().max(500).default(''),
  reportDelivery: z.boolean().default(false),
});
export type BookingRequest = z.infer<typeof BookingRequest>;

/** The lab's answer. A lab may approve for another time than asked, and say why. */
export const BookingDecision = z.object({
  approve: z.boolean(),
  comment: z.string().trim().max(500).default(''),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  time: Time.optional(),
});
export type BookingDecision = z.infer<typeof BookingDecision>;

/** Crockford letters without look-alikes, so a code survives being read out at the counter. */
const CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export function newBookingCode(random: (n: number) => Uint8Array): string {
  const b = random(6);
  return `B-${[...b].map((x) => CODE_ALPHABET[x % CODE_ALPHABET.length]).join('')}`;
}
